import React, { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Header } from '../../components/header';
import useStyles from './styles';
import SearchBar from '../../components/search-bar';
import { AppState } from '../../store';
import TimeRange from '../../components/time-range';
import TransactionsTable from '../../components/transactions/transactions-table';
import DeviceDetailQuickLook from '../../components/transactions/device-detail-quick-look';
import ActiveFilters from '../../components/transactions/active-filters';
import { Search, User, FilterHeader, Time, Help } from '../../components/header/components';
import DeviceLabel from '../../components/device-label';
import ExportTransactions from '../../components/transactions/export-transactions';
import ReloadIcon from '../../assets/icons/reload-icon';
import theme from '../../globals/theme';
import ColumnSortVisibility from '../../components/virtualized-table/components/column-sort-visibility';
import {
  headCells,
  sortableAccessors,
} from '../../components/transactions/transactions-table/head-cells';
import { useMountEffect } from '../../globals/hooks/use-mount-effect';
import { setSelectedEntityFromDeviceOrTelematicsId } from '../../store/search/actions';
import { uniqueId } from '../../globals/utils';
import DealerTransactionsTable from '../../components/transactions/dealer-transactions-table/index';

const TransactionsInfiniteScroll = () => {
  const history = useHistory();
  const { deviceId } = useParams<{ deviceId: string }>();
  if(deviceId === undefined) {
    history.push(`/`);
  }
  const dispatch = useDispatch();
  const classes = useStyles();
  const recordCount = useSelector((state: AppState) => state.transactions.count);
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const table: any = useRef<HTMLElement>(null);
  const [columnVisibility, setColumnVisibility] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [resetTableString, setResetTableString] = useState(uniqueId());
  const storageString = 'transaction-table';
  const savedHidden = window.localStorage.getItem(`${storageString}-hidden-columns`);
  const parameters: URLSearchParams = new URLSearchParams(history.location.search);
  const dealerCode: any = parameters.get('dealerCode') !== null ? parameters.get('dealerCode') : '';
  useMountEffect(() => {
    if (deviceId && dealerCode.length === 0) dispatch(setSelectedEntityFromDeviceOrTelematicsId(deviceId));
    const savedHiddenItems = savedHidden && savedHidden !== 'null' ? JSON.parse(savedHidden) : [];
    if (!savedHiddenItems.includes('fileSize')) {
      savedHiddenItems.push('fileSize');
    }
    window.localStorage.setItem(
      `${storageString}-hidden-columns`,
      JSON.stringify(savedHiddenItems),
    );
  });

  const columnController = {
    storageString,
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    headerColumns: headCells,
    sortableAccessors,
    reloadTable: () => setResetTableString(uniqueId),
  };
  return (
    <>
      <Header
        title="Transactions"
        headerComponents={
          selectedEntity || dealerCode.length > 0
            ? [<Help userLink='/SitePages/Transaction-Page-Manual.aspx?promotedState=0'/>, <Search page="transactions" />, <FilterHeader />, <Time />, <User />]
            : [<Help userLink='/SitePages/Search-Page-Manual.aspx'/>, <Time suppressPopover />, <User />]
        }
      />
      {!selectedEntity && !deviceId ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 5vh - 64px)"
          paddingBottom="5vh"
        >
          <SearchBar page="transactions" />
        </Box>
      ) : (
        <Box className={classes.scrollBox}>
          <Box
            className={classes.margin}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Box display="flex" alignItems="center">
              <DeviceLabel />
              <Box flexGrow={1} display="flex" alignItems="center">
                {dealerCode.length === 0 && <DeviceDetailQuickLook />}
                <Tooltip title="Refresh Data" enterDelay={600}>
                  <span>
                    <IconButton
                      onClick={() => setResetTableString(uniqueId())}
                      id="reloadButton"
                      size="large"
                    >
                      <ReloadIcon stroke={`${theme.palette.grey[600]}`} />
                    </IconButton>
                  </span>
                </Tooltip>
                  <ActiveFilters vimsDataFilter={dealerCode && dealerCode.length > 0} page='transation' />
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              <Box pr={2}>
                {!!recordCount && recordCount > 0 && (
                  <Typography className={classes.count}>
                    {`${recordCount} Record${recordCount > 1 ? 's' : ''}`}
                  </Typography>
                )}
              </Box>
              <TimeRange />
              <Tooltip title="Export Transactions" enterDelay={600}>
                <span>
                  <ExportTransactions />
                </span>
              </Tooltip>
              <ColumnSortVisibility {...columnController} />
            </Box>
          </Box>
          <div className={classes.tableContainer} ref={table}>
            {dealerCode.length === 0 && (
              <>
                <TransactionsTable
                  headCells={headCells}
                  columnOrder={['expander', ...columnOrder]}
                  columnVisibility={columnVisibility}
                  resetTableString={resetTableString}
                  tableContainer={table}
                  storageString={storageString}
                  id="transactionsTable"
                />
              </>
            )}
            {dealerCode.length > 0 && (
              <>
                <DealerTransactionsTable
                  columnOrder={['expander', ...columnOrder]}
                  dealerCode={dealerCode}
                  columnVisibility={columnVisibility}
                  resetTableString={resetTableString}
                  tableContainer={table}
                  storageString={storageString}
                  id="dealerTransactionsTable"
                />
              </>
            )}
          </div>
        </Box>
      )}
    </>
  );
};
export default TransactionsInfiniteScroll;
